import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, FileDown, PackageCheck, Pencil, Send, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMaster } from "@/lib/useMaster";
import { formatDateTime, formatIDR } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STEPS = ["Draft", "Sent", "Received"];

function Stepper({ status }) {
  const idx = status === "draft" ? 0 : status === "dikirim" ? 1 : 2;
  return (
    <div className="flex items-center" data-testid="transfer-stepper">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex items-center gap-2">
            <div
              className={`grid h-7 w-7 place-items-center rounded-full border text-xs font-bold ${
                i < idx
                  ? "border-blue-600 bg-blue-600 text-white"
                  : i === idx
                    ? "border-blue-600 bg-white text-blue-600"
                    : "border-zinc-300 bg-white text-zinc-400"
              }`}
              data-testid={`step-${s.toLowerCase()}`}
            >
              {i < idx ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={`text-sm font-medium ${i <= idx ? "text-zinc-900" : "text-zinc-400"}`}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`mx-3 h-px w-10 sm:w-16 ${i < idx ? "bg-blue-600" : "bg-zinc-300"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function TransferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { warehouses, productMap, warehouseMap } = useMaster();
  const [tr, setTr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [ef, setEf] = useState({ from: "", to: "", note: "", items: [] });
  const [editStock, setEditStock] = useState([]);

  const openEdit = () => {
    setEf({
      from: tr.from_warehouse_id,
      to: tr.to_warehouse_id,
      note: tr.note || "",
      items: tr.items.map((i) => ({ product_id: i.product_id, qty: String(i.qty) })),
    });
    setEditOpen(true);
  };

  const setEfItem = (i, patch) => {
    setEf({ ...ef, items: ef.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const validItems = ef.items.filter((it) => it.product_id && parseInt(it.qty, 10) > 0);
    if (validItems.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(`/transfers/${tr.id}`, {
        from_warehouse_id: ef.from,
        to_warehouse_id: ef.to,
        note: ef.note,
        items: validItems.map((it) => ({ product_id: it.product_id, qty: parseInt(it.qty, 10) })),
      });
      setTr(data);
      setEditOpen(false);
      toast.success("Delivery note updated");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(() => {
    api.get(`/transfers/${id}`).then((r) => setTr(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!editOpen || !ef.from) {
      setEditStock([]);
      return;
    }
    api.get(`/reports/stock?warehouse_id=${ef.from}`).then((r) => setEditStock(r.data.rows)).catch(() => {});
  }, [editOpen, ef.from]);

  const editAvail = Object.fromEntries(editStock.map((r) => [r.product_id, r.qty]));

  const editOptions = (currentId) => {
    const opts = editStock.map((r) => ({ value: r.product_id, label: `${productMap[r.product_id]?.name} — stock: ${r.qty}` }));
    if (currentId && !opts.some((o) => o.value === currentId)) {
      opts.push({ value: currentId, label: `${productMap[currentId]?.name || "Unknown"} — no stock in source` });
    }
    return opts;
  };

  const act = async (action, successMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/transfers/${id}/${action}`);
      setTr(data);
      toast.success(successMsg);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete delivery note ${tr.sj_no}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/transfers/${tr.id}`);
      toast.success("Delivery note deleted");
      navigate("/transfer");
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  if (!tr) {
    return (
      <div className="grid h-64 place-items-center" data-testid="transfer-detail-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-blue-600" />
      </div>
    );
  }

  const fromWh = warehouseMap[tr.from_warehouse_id];
  const toWh = warehouseMap[tr.to_warehouse_id];
  const totalQty = tr.items.reduce((s, i) => s + i.qty, 0);
  const totalCost = tr.items.reduce(
    (s, i) => s + (i.allocations || []).reduce((x, a) => x + a.qty * a.cost, 0),
    0
  );

  return (
    <div className="space-y-6" data-testid="transfer-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/transfer")} data-testid="btn-back-transfer" className="rounded-sm border border-zinc-200 bg-white p-2 text-zinc-500 transition-colors hover:text-zinc-900">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-mono text-2xl font-bold tracking-tight text-zinc-900" data-testid="transfer-sj-no">{tr.sj_no}</h1>
              <StatusBadge status={tr.status} />
            </div>
            <p className="mt-1 text-sm text-zinc-500">Created {formatDateTime(tr.created_at)} · by <span className="font-medium text-zinc-700" data-testid="transfer-created-by">{tr.created_by_name}</span>{tr.edited_by_name ? <span data-testid="transfer-edited-by"> · edited by {tr.edited_by_name}</span> : null}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tr.status === "draft" && !tr.deleted && (
            <>
              <Button data-testid="btn-send-transfer" disabled={busy} onClick={() => act("send", "Delivery note sent — source warehouse stock deducted via FIFO")} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
                <Send className="mr-2 h-4 w-4" /> Send (Consume FIFO)
              </Button>
              <Button data-testid="btn-cancel-transfer" disabled={busy} variant="outline" onClick={() => act("cancel", "Delivery note cancelled")} className="rounded-sm border-zinc-300 text-zinc-600">
                <XCircle className="mr-2 h-4 w-4" /> Cancel
              </Button>
            </>
          )}
          {tr.status === "dikirim" && !tr.deleted && (
            <Button data-testid="btn-receive-transfer" disabled={busy} onClick={() => act("receive", "Goods received — stock added to destination warehouse")} className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700">
              <PackageCheck className="mr-2 h-4 w-4" /> Receive at Destination
            </Button>
          )}
          {tr.status === "dibatalkan" && !tr.deleted && user.role === "owner" && (
            <Button data-testid="btn-delete-transfer" disabled={busy} variant="outline" onClick={handleDelete} className="rounded-sm border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
          {tr.status === "draft" && !tr.deleted && user.role === "owner" && (
            <Button data-testid="btn-edit-transfer" disabled={busy} variant="outline" onClick={openEdit} className="rounded-sm border-zinc-300 text-zinc-600">
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
          )}
          {tr.status === "draft" && !tr.deleted && user.role === "owner" && (
            <Button data-testid="btn-delete-transfer" disabled={busy} variant="outline" onClick={handleDelete} className="rounded-sm border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
          <Button
            data-testid="btn-print-sj"
            variant="outline"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/transfers/${tr.id}/pdf`, "_blank")}
            className="rounded-sm border-zinc-300"
          >
            <FileDown className="mr-2 h-4 w-4" /> Print PDF
          </Button>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Delivery Note {tr.sj_no}</DialogTitle>
            <DialogDescription className="sr-only">Edit draft delivery note route, note, and items</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Warehouse</Label>
                <select data-testid="edit-transfer-from" required value={ef.from} onChange={(e) => setEf({ ...ef, from: e.target.value })} className="h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>To Warehouse</Label>
                <select data-testid="edit-transfer-to" required value={ef.to} onChange={(e) => setEf({ ...ef, to: e.target.value })} className="h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Transfer Items</Label>
                <button type="button" data-testid="edit-transfer-add-item" onClick={() => setEf({ ...ef, items: [...ef.items, { product_id: "", qty: "" }] })} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                  + Add Item
                </button>
              </div>
              {ef.items.map((it, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`edit-transfer-item-row-${i}`}>
                  <div className="flex-1">
                    <SearchableSelect
                      testid={`edit-transfer-item-product-${i}`}
                      options={editOptions(it.product_id)}
                      value={it.product_id}
                      onChange={(v) => setEfItem(i, { product_id: v })}
                      placeholder="Select product..."
                    />
                  </div>
                  <Input data-testid={`edit-transfer-item-qty-${i}`} type="number" min="1" max={editAvail[it.product_id] || undefined} required value={it.qty} onChange={(e) => setEfItem(i, { qty: e.target.value })} placeholder="Qty" className="w-28 rounded-sm border-zinc-300" />
                  {ef.items.length > 1 && (
                    <button type="button" data-testid={`edit-transfer-item-remove-${i}`} onClick={() => setEf({ ...ef, items: ef.items.filter((_, idx) => idx !== i) })} className="rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input data-testid="edit-transfer-note" value={ef.note} onChange={(e) => setEf({ ...ef, note: e.target.value })} placeholder="Optional note" className="rounded-sm border-zinc-300" />
            </div>
            <p className="text-xs text-zinc-400">Only Draft delivery notes can be edited — no stock has moved yet.</p>
            <Button data-testid="edit-transfer-submit" type="submit" disabled={busy} className="w-full rounded-sm bg-blue-600 text-white hover:bg-blue-700">
              {busy ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {tr.deleted ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="transfer-deleted-banner">
          This delivery note has been deleted{tr.deleted_by_name ? ` by ${tr.deleted_by_name}` : ""}{tr.deleted_at ? ` on ${formatDateTime(tr.deleted_at)}` : ""}. Record kept for audit.
        </div>
      ) : tr.status === "dibatalkan" ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="transfer-cancelled-banner">
          This delivery note has been cancelled.
        </div>
      ) : (
        <div className="rounded-md border border-zinc-200 bg-white px-6 py-4">
          <Stepper status={tr.status} />
        </div>
      )}

      <div className="mx-auto max-w-4xl border border-zinc-200 bg-white p-8 md:p-12" data-testid="sj-document">
        <div className="flex items-start justify-between border-b-2 border-blue-600 pb-6">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">HORIZON</h2>
            <p className="mt-1 text-sm text-zinc-500">Phone & Laptop Store</p>
          </div>
          <div className="text-right">
            <p className="font-heading text-xl font-bold tracking-tight text-zinc-900">DELIVERY NOTE</p>
            <p className="mt-1 font-mono text-sm text-blue-600">{tr.sj_no}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-6 text-sm md:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400">From Warehouse</p>
            <p className="mt-1 font-semibold text-zinc-900" data-testid="sj-from">{fromWh?.name}</p>
            <p className="text-zinc-500">{fromWh?.address}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400">To Warehouse</p>
            <p className="mt-1 font-semibold text-zinc-900" data-testid="sj-to">{toWh?.name}</p>
            <p className="text-zinc-500">{toWh?.address}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400">Sent / Received At</p>
            <p className="mt-1 text-zinc-700">{tr.sent_at ? `${formatDateTime(tr.sent_at)} · by ${tr.sent_by_name}` : "-"}</p>
            <p className="text-zinc-700" data-testid="transfer-received-by">{tr.received_at ? `${formatDateTime(tr.received_at)} · by ${tr.received_by_name}` : "-"}</p>
          </div>
        </div>

        {tr.note && (
          <p className="mt-4 rounded-sm bg-zinc-50 px-3 py-2 text-sm text-zinc-600">Note: {tr.note}</p>
        )}

        <table className="mt-6 w-full text-sm" data-testid="sj-items-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-3 py-2.5 font-medium">Product</th>
              <th className="px-3 py-2.5 font-medium">FIFO Batch</th>
              <th className="px-3 py-2.5 text-right font-medium">Qty</th>
              <th className="px-3 py-2.5 text-right font-medium">Value (COGS)</th>
            </tr>
          </thead>
          <tbody>
            {tr.items.map((it, i) => {
              const cost = (it.allocations || []).reduce((s, a) => s + a.qty * a.cost, 0);
              return (
                <tr key={i} className="border-b border-zinc-100 last:border-0">
                  <td className="px-3 py-2.5 font-medium text-zinc-900">{productMap[it.product_id]?.name || "?"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-zinc-500">
                    {(it.allocations || []).length > 0
                      ? it.allocations.map((a) => `${a.batch_no} ×${a.qty}`).join(", ")
                      : "— (allocated when sent)"}
                  </td>
                  <td className="tnum px-3 py-2.5 text-right text-zinc-700">{it.qty}</td>
                  <td className="tnum px-3 py-2.5 text-right text-zinc-700">{cost > 0 ? formatIDR(cost) : "-"}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-zinc-200">
              <td colSpan={2} className="px-3 py-2.5 text-sm font-bold text-zinc-900">Total</td>
              <td className="tnum px-3 py-2.5 text-right text-sm font-bold text-zinc-900" data-testid="sj-total-qty">{totalQty} units</td>
              <td className="tnum px-3 py-2.5 text-right text-sm font-bold text-zinc-900">{totalCost > 0 ? formatIDR(totalCost) : "-"}</td>
            </tr>
          </tfoot>
        </table>

        <div className="mt-12 grid grid-cols-2 gap-6 text-center text-sm text-zinc-600">
          <div>
            <p>Shipper,</p>
            <p className="mt-14 font-medium text-zinc-400">( _________________ )</p>
          </div>
          <div>
            <p>Receiver,</p>
            <p className="mt-14 font-medium text-zinc-400">( _________________ )</p>
          </div>
        </div>
      </div>
    </div>
  );
}
